import { useCallback } from "react";
import useWheelBrandValidation from "./useWheelBrandValidation";

/**
 * Props interface for useWheelBrandHandlers hook
 */
interface UseWheelBrandHandlersProps<T> {
  /**
   * Callback function to handle data updates
   * @param identifier - The identifier (can be string or number)
   * @param key - The field key to update
   * @param data - The new value (string or number)
   */
  onUpdate: (identifier: string | number, key: keyof T, data: string | number) => void;
}

/**
 * Custom hook that provides event handlers for editable table cells
 *
 * This hook encapsulates all the logic for handling user interactions with editable cells,
 * including text input, number input, validation, cursor positioning, and data formatting.
 * @param props - Configuration object containing the onUpdate callback
 * @returns Object containing all event handlers and utility functions
 */
const useWheelBrandHandlers = <T extends Record<string, any>>({
  onUpdate,
}: UseWheelBrandHandlersProps<T>) => {
  const {
    getError,
    validateTextField,
    validateNumberField,
    parseNumber,
    formatInputText,
    removeCommas,
  } = useWheelBrandValidation<T>();

  /**
   * Handles blur events for text input cells
   *
   * Validates the field and triggers the update callback if no validation errors exist.
   * This handler is used for text fields like maker_name and brand_name.
   * @param e - Focus event from the table cell
   * @param field - The field name being edited
   * @param identifier - The identifier (can be string or number)
   * @param value - The current text value
   * @param index - The row index for error tracking
   */
  const handleTextBlur = useCallback(
    async (
      e: React.FocusEvent<HTMLTableCellElement>,
      field: string,
      identifier: string | number,
      value: string,
      index: number,
    ) => {
      if (getError(field, index)) return;
      onUpdate(identifier, field as keyof T, value);
    },
    [getError, onUpdate],
  );

  /**
   * Handles blur events for number input cells
   *
   * Validates the field, handles empty values by setting them to "0" for specific fields,
   * and triggers the update callback if no validation errors exist.
   * This handler is used for numeric fields like fixed_discount and sale_price_rate_cost.
   * @param e - Focus event from the table cell
   * @param field - The field name being edited
   * @param identifier - The identifier (can be string or number)
   * @param value - The parsed number value
   * @param index - The row index for error tracking
   */
  const handleNumberBlur = useCallback(
    async (
      e: React.FocusEvent<HTMLTableCellElement>,
      field: string,
      identifier: string | number,
      value: number,
      index: number,
    ) => {
      if (getError(field, index)) return;

      const target = e.target as HTMLTableCellElement;
      const isClearableField = [
        "fixed_discount",
        "sale_price_rate_cost",
        "wheel_selling_price",
      ].includes(field);
      // Strip every non-digit so that residual `<br>` / whitespace inserted by the browser
      // into an emptied contentEditable cell still registers as cleared.
      const trimmedDigits = target.innerText.replace(/[^0-9]/g, "");
      const isEmpty = trimmedDigits.length === 0;

      if (isEmpty && isClearableField) {
        onUpdate(identifier, field as keyof T, "");
        return;
      }

      onUpdate(identifier, field as keyof T, value);
    },
    [getError, onUpdate],
  );

  /**
   * Handles input events for text cells
   *
   * Performs real-time validation for text fields as the user types.
   * Currently supports validation for maker_name and brand_name fields.
   * @param e - Form event from the table cell
   * @param field - The field name being edited
   * @param index - The row index for error tracking
   */
  const handleTextInput = useCallback(
    (e: React.FormEvent<HTMLTableCellElement>, field: string, index: number) => {
      const target = e.target as HTMLTableCellElement;
      if ((field === "maker_name" || field === "brand_name") && target) {
        const value = target.innerText;
        validateTextField(value, field, index);
      }
    },
    [validateTextField],
  );

  /**
   * Handles keydown events for number input cells
   *
   * Filters keyboard input to only allow numeric characters and navigation keys.
   * Prevents invalid characters from being entered into number fields.
   *
   * Allowed keys:
   * - Digits: 0-9
   * - Navigation: Arrow keys, Home, End, Tab, Enter, Escape
   * - Editing: Backspace, Delete
   * - Shortcuts: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
   * @param e - Keyboard event
   * @param field - The field name being edited
   * @param index - The row index for error tracking
   */
  const handleNumberKeyDown = useCallback((e: any, field: string, index: number) => {
    const { key, ctrlKey, metaKey } = e;

    // Allow backspace, delete, arrow keys, tab, etc.
    if (
      [
        "Backspace",
        "Delete",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Home",
        "End",
        "Tab",
        "Enter",
        "Escape",
      ].includes(key)
    ) {
      return; // Let browser handle these keys naturally
    }

    // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
    if (ctrlKey || metaKey) {
      return;
    }

    // Only allow digits
    if (!/^[0-9]$/.test(key)) {
      e.preventDefault();
    }

    // If it's a valid digit, let it go through naturally
    // The onInput handler will handle formatting
  }, []);

  /**
   * Handles input events for number cells
   *
   * Formats the input text (adds commas for thousands), validates the field,
   * and maintains cursor position after formatting. This handler ensures
   * that number inputs are properly formatted and the cursor doesn't jump
   * when the text is reformatted.
   *
   * Key features:
   * - Real-time number formatting with commas
   * - Cursor position preservation after formatting
   * - Validation updates
   * - Error handling for cursor positioning
   * @param e - Input event from the table cell
   * @param field - The field name being edited
   * @param index - The row index for error tracking
   */
  const handleNumberInput = useCallback(
    (e: any, field: string, index: number) => {
      const { target } = e;

      // Get current selection
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);
      const cursorPosition = range.startOffset;

      // Get current text
      const currentText = target.innerText || "";
      const formattedText = formatInputText(currentText);

      // Only proceed if text actually changed
      if (currentText === formattedText) {
        validateNumberField(formattedText, field, index);
        return;
      }

      // Update text content
      target.innerText = formattedText;

      /**
       * Restores the cursor position after DOM update
       *
       * This function handles various edge cases:
       * - Empty text: places cursor at start
       * - Text with content: places cursor at appropriate position
       * - Error handling: falls back gracefully if positioning fails
       */
      const restoreCursor = () => {
        try {
          const newSelection = window.getSelection();
          if (!newSelection) return;

          const newRange = document.createRange();

          if (formattedText.length === 0) {
            // For empty text, place cursor at start
            newRange.selectNodeContents(target);
            newRange.collapse(true);
          } else {
            // Find the text node and place cursor
            const textNode = target.firstChild;
            if (textNode && textNode.nodeType === Node.TEXT_NODE) {
              const maxPos = textNode.textContent?.length || 0;
              const safePos = Math.min(cursorPosition, maxPos);
              newRange.setStart(textNode, safePos);
              newRange.collapse(true);
            } else {
              // Fallback: place at start
              newRange.selectNodeContents(target);
              newRange.collapse(true);
            }
          }

          newSelection.removeAllRanges();
          newSelection.addRange(newRange);
        } catch (error) {
          console.error("Cursor positioning failed:", error);
        }
      };

      // Use micro-task to ensure DOM is fully updated
      Promise.resolve().then(restoreCursor);

      // Update validation
      validateNumberField(formattedText, field, index);
    },
    [formatInputText, validateNumberField],
  );

  /**
   * Handles focus events for both text and number cells
   *
   * Removes comma formatting from the cell content when the user focuses on it,
   * making it easier to edit the raw number value.
   * @param e - Focus event from the table cell
   */
  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      e.target.innerText = removeCommas(e.target.innerText);
    },
    [removeCommas],
  );

  return {
    handleTextBlur,
    handleNumberBlur,
    handleTextInput,
    handleNumberInput,
    handleNumberKeyDown,
    handleFocus,
    getError,
    parseNumber,
  };
};

export default useWheelBrandHandlers;
